import React from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { FormField } from '../../../types/formBuilder';

interface AdvancedFieldSettingsProps {
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
}

export const AdvancedFieldSettings: React.FC<AdvancedFieldSettingsProps> = ({ field, onUpdate }) => {
  const addAdditionalVariable = () => {
    const newVariables = [...(field.additional_variables || []), ''];
    onUpdate({ additional_variables: newVariables });
  };

  const updateAdditionalVariable = (index: number, value: string) => {
    const newVariables = [...(field.additional_variables || [])];
    newVariables[index] = value;
    onUpdate({ additional_variables: newVariables });
  };

  const removeAdditionalVariable = (index: number) => {
    const newVariables = [...(field.additional_variables || [])];
    newVariables.splice(index, 1);
    onUpdate({ additional_variables: newVariables });
  };

  const updateAdditionalVariableType = (varName: string, type: 'string' | 'list') => {
    const newTypes = { ...(field.additional_variable_types || {}), [varName]: type };
    onUpdate({ additional_variable_types: newTypes });
  };

  return (
    <div className="space-y-6">
      {/* Field Flags */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-th-primary">Field Properties</h4>
        
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate({ required: e.target.checked })}
              className="w-4 h-4 text-brand bg-elevated border-subtle rounded focus:ring-brand focus:ring-2"
            />
            <div>
              <span className="text-th-primary font-medium">Required field</span>
              <p className="text-th-secondary text-xs">Users must fill this field</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={field.advanced || false}
              onChange={(e) => onUpdate({ advanced: e.target.checked })}
              className="w-4 h-4 text-accent bg-elevated border-subtle rounded focus:ring-accent focus:ring-2"
            />
            <div>
              <span className="text-th-primary font-medium">Advanced setting</span>
              <p className="text-th-secondary text-xs">Show in advanced section</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={field.hidden || false}
              onChange={(e) => onUpdate({ hidden: e.target.checked })}
              className="w-4 h-4 text-status-warning bg-elevated border-subtle rounded focus:ring-status-warning focus:ring-2"
            />
            <div>
              <span className="text-th-primary font-medium">Hidden field</span>
              <p className="text-th-secondary text-xs">Hide from UI, send to API with default value</p>
            </div>
          </label>
        </div>
      </div>

      {/* Default Value */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-th-primary">
          Default Value
        </label>
        <input
          type="text"
          value={field.defaultValue || ''}
          onChange={(e) => onUpdate({ defaultValue: e.target.value })}
          placeholder="Default value"
          className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
        />
        <p className="text-xs text-th-secondary">
          Pre-filled value for this field
        </p>
      </div>

      {/* Additional API Variables */}
      <div className="space-y-4 p-4 bg-status-warning/5 border border-status-warning/20 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-th-primary">Additional API Variables (Optional)</h4>
            <p className="text-xs text-th-secondary">
              Send this field's value to multiple API parameters
            </p>
          </div>
          <button
            onClick={addAdditionalVariable}
            className="flex items-center space-x-1 px-3 py-1.5 bg-status-warning text-black rounded-lg text-sm font-medium hover:bg-status-warning/90 transition-all"
          >
            <Plus className="w-3 h-3" />
            <span>Add Variable</span>
          </button>
        </div>

        <div className="space-y-3">
          {(field.additional_variables || []).map((variable, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-elevated border border-subtle rounded-lg">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={variable}
                  onChange={(e) => updateAdditionalVariable(index, e.target.value)}
                  placeholder="api_parameter_name"
                  className="bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm font-mono"
                />
                <select
                  value={field.additional_variable_types?.[variable] || 'string'}
                  onChange={(e) => updateAdditionalVariableType(variable, e.target.value as 'string' | 'list')}
                  className="bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
                >
                  <option value="string">String</option>
                  <option value="list">List/Array</option>
                </select>
              </div>
              <button
                onClick={() => removeAdditionalVariable(index)}
                className="p-2 text-th-secondary hover:text-status-error transition-colors rounded hover:bg-status-error/10"
                title="Remove variable"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {(!field.additional_variables || field.additional_variables.length === 0) && (
          <div className="text-center py-4 text-th-secondary">
            <p className="text-sm">No additional variables</p>
            <p className="text-xs">This field's value will only be sent to the main variable</p>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-status-warning/10 border border-status-warning/30 rounded p-3">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-4 h-4 text-status-warning" />
            <span className="text-status-warning font-medium text-sm">How Additional Variables Work</span>
          </div>
          <ul className="text-th-secondary text-xs space-y-1 list-disc list-inside">
            <li>The field's value will be sent to multiple API parameters</li>
            <li>Use "String" type to send the exact value</li>
            <li>Use "List/Array" type to wrap the value in an array</li>
            <li>Useful for APIs that expect the same value in multiple places</li>
          </ul>
        </div>
      </div>
    </div>
  );
};